import { css } from "@emotion/react";
import { LanguageSelector } from "./language-selector";
import { type ReactNode, useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@swan-io/chicane";
import { useOrganisation } from "../lib/organisation";
import { Router } from "../App";
import { Button } from "./button";
import { House, Target, Users, UserList, CheckCircle } from "@phosphor-icons/react";
import { slugify } from "../lib/slugify";

const layoutContainer = css`
  display: flex;
  width: 100%;
  min-height: 100vh;
`;

const layoutSidebar = css`
  width: 280px;
  background: var(--color-navy);
  box-shadow: 4px 0 16px var(--shadow-light);
  display: flex;
  flex-direction: column;
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  z-index: 100;
  overflow-y: auto;

  @media (max-width: 768px) {
    width: 240px;
  }

  @media (max-width: 640px) {
    transform: translateX(-100%);
    transition: transform 0.3s ease;
    width: 280px;
  }
`;

const layoutSidebarOpen = css`
  @media (max-width: 640px) {
    transform: translateX(0);
  }
`;

const layoutMain = css`
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  @media (max-width: 768px) {
    margin-left: 240px;
  }

  @media (max-width: 640px) {
    margin-left: 0;
  }
`;

const layoutContent = css`
  flex: 1;
  padding: 40px;
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 24px;
  }

  @media (max-width: 640px) {
    padding: 16px;
    padding-top: 64px;
  }
`;

const sidebarHeaderStyle = css`
  padding: 32px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 640px) {
    padding: 24px 20px;
  }
`;

const sidebarLogo = css`
  font-family: var(--font-heading);
  font-size: 1.75rem;
  font-weight: 900;
  color: #FFFFFF;
  margin: 0;
`;

const sidebarTagline = css`
  font-size: var(--text-sm);
  color: rgba(255, 255, 255, 0.6);
  margin: 4px 0 0 0;
`;

const sidebarOrgSelect = css`
  margin-top: 12px;
  width: 100%;
  padding: 8px 12px;
  font-size: var(--text-sm);
  font-family: var(--font-heading);
  font-weight: 700;
  color: #FFFFFF;
  background: var(--color-navy-mid);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.3);
  }

  &:focus {
    outline: none;
    border-color: var(--color-gold);
  }

  & option {
    background: var(--color-navy);
    color: #FFFFFF;
  }
`;

const sidebarAddOrgBtn = css`
  display: block;
  width: 100%;
  margin-top: 8px;
  padding: 8px 12px;
  font-size: var(--text-sm);
  font-family: var(--font-heading);
  font-weight: 700;
  color: rgba(255, 255, 255, 0.6);
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: var(--radius-md);
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:hover {
    border-color: rgba(255, 255, 255, 0.3);
    color: #FFFFFF;
    background: rgba(255, 255, 255, 0.05);
  }
`;

const sidebarNav = css`
  flex: 1;
  padding: 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const navLinkStyle = css`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: var(--text-sm);
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #FFFFFF;
  }

  &.nav-link-active {
    background: rgba(255, 255, 255, 0.1);
    color: #FFFFFF;
  }
`;

const navSubLinkStyle = css`
  padding-left: 48px;
  font-size: var(--text-xs);
  font-weight: 600;
`;

const sidebarLanguageToggle = css`
  padding: 16px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const sidebarUser = css`
  padding: 16px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (max-width: 640px) {
    padding: 12px 16px;
  }
`;

const sidebarUserInfo = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const sidebarUserName = css`
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: var(--text-sm);
  color: #FFFFFF;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const sidebarUserEmail = css`
  font-size: var(--text-xs);
  color: rgba(255, 255, 255, 0.5);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const sidebarLogoutBtn = css`
  font-size: 0.8rem;
  padding: 6px 12px;
  width: 100%;
`;

const sidebarToggle = css`
  display: none;
  position: fixed;
  top: 12px;
  left: 12px;
  z-index: 200;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: var(--radius-md);
  background: var(--container-bg);
  box-shadow: 0 2px 8px var(--shadow-medium);
  cursor: pointer;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  transition:
    background 0.3s ease,
    box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px var(--shadow-dark);
  }

  @media (max-width: 640px) {
    display: flex;
  }
`;

const sidebarToggleIcon = css`
  display: block;
  width: 22px;
  height: 2px;
  background: var(--text-primary);
  border-radius: 2px;
  transition: all 0.3s ease;
  position: relative;

  &::before,
  &::after {
    content: "";
    display: block;
    width: 22px;
    height: 2px;
    background: var(--text-primary);
    border-radius: 2px;
    transition: all 0.3s ease;
    position: absolute;
    left: 0;
  }

  &::before {
    top: -7px;
  }

  &::after {
    top: 7px;
  }
`;

const sidebarToggleIconOpen = css`
  background: transparent;

  &::before {
    top: 0;
    transform: rotate(45deg);
  }

  &::after {
    top: 0;
    transform: rotate(-45deg);
  }
`;

const sidebarOverlay = css`
  display: none;
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 99;
  backdrop-filter: blur(2px);

  @media (max-width: 640px) {
    display: block;
  }
`;

const layoutFooter = css`
  width: 100%;
  background: var(--container-bg);
  border-top: 1px solid var(--color-border);
  padding: 24px 40px;
  margin-top: auto;
  transition: background 0.3s ease;

  @media (max-width: 768px) {
    padding: 20px 24px;
  }

  @media (max-width: 640px) {
    padding: 16px;
  }
`;

const footerContent = css`
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;

  & p {
    margin: 0;
    color: var(--text-secondary);
    font-size: var(--text-sm);
    transition: color 0.3s ease;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }

  @media (max-width: 640px) {
    flex-direction: column;
    text-align: center;
    gap: 8px;
  }
`;

const footerLinks = css`
  display: flex;
  gap: 8px;
  align-items: center;

  & a {
    color: var(--text-secondary);
    text-decoration: none;
    font-size: var(--text-sm);
    transition: color 0.2s ease;

    &:hover {
      color: var(--text-primary);
    }
  }
`;

const footerSeparator = css`
  color: var(--text-secondary);
  opacity: 0.5;
`;

interface LayoutProps {
  children: ReactNode;
  user?: { username?: string; email?: string } | null;
  onLogout?: () => void;
}

export function Layout({ children, user, onLogout }: LayoutProps) {
  const { t } = useTranslation();
  const { organisations, currentOrganisation, setCurrentOrganisation } = useOrganisation();
  const orgSlug = currentOrganisation ? slugify(currentOrganisation.name) : "";
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  // Close sidebar on route changes (when children change)
  useEffect(() => {
    closeSidebar();
  }, [children, closeSidebar]);

  // Close sidebar on escape key
  useEffect(() => {
    if (!sidebarOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeSidebar();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [sidebarOpen, closeSidebar]);

  return (
    <div css={layoutContainer}>
      {/* Mobile hamburger button */}
      <button
        css={sidebarToggle}
        onClick={() => setSidebarOpen((prev) => !prev)}
        aria-label={sidebarOpen ? t("layout.closeSidebar") : t("layout.openSidebar")}
      >
        <span css={[sidebarToggleIcon, sidebarOpen && sidebarToggleIconOpen]} />
      </button>

      {/* Sidebar overlay for mobile */}
      {sidebarOpen && <div css={sidebarOverlay} onClick={closeSidebar} aria-hidden="true" />}

      {/* Sidebar */}
      <aside css={[layoutSidebar, sidebarOpen && layoutSidebarOpen]}>
        <div css={sidebarHeaderStyle}>
          <h2 css={sidebarLogo}>Carton Plein</h2>
          <p css={sidebarTagline}>{t("layout.tagline")}</p>
          {organisations.length > 0 && (
            <select
              css={sidebarOrgSelect}
              value={currentOrganisation?.id ?? ""}
              onChange={(e) => {
                const org = organisations.find((o) => o.id === e.target.value);
                if (org) {
                  setCurrentOrganisation(org);
                  Router.push("Homepage", { orgSlug: slugify(org.name) });
                }
              }}
            >
              {organisations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          )}
          <Link to="/organisations/create" css={sidebarAddOrgBtn}>
            {t("layout.newOrganisation")}
          </Link>
        </div>

        <nav css={sidebarNav}>
          <Link to={`/org/${orgSlug}`} css={navLinkStyle} activeClassName="nav-link-active">
            <House size={20} />
            <span>{t("layout.navEvents")}</span>
          </Link>
          <Link to={`/org/${orgSlug}/grids`} css={navLinkStyle} activeClassName="nav-link-active">
            <Target size={20} />
            <span>{t("layout.navGrids")}</span>
          </Link>
          <Link to={`/org/${orgSlug}/grids/validate`} css={[navLinkStyle, navSubLinkStyle]} activeClassName="nav-link-active">
            <CheckCircle size={18} />
            <span>{t("layout.navGridsToValidate")}</span>
          </Link>
          <Link to={`/org/${orgSlug}/players`} css={navLinkStyle} activeClassName="nav-link-active">
            <UserList size={20} />
            <span>{t("layout.navPlayers")}</span>
          </Link>
          <Link to={`/org/${orgSlug}/members`} css={navLinkStyle} activeClassName="nav-link-active">
            <Users size={20} />
            <span>{t("layout.navMembers")}</span>
          </Link>
        </nav>

        <div css={sidebarLanguageToggle}>
          <LanguageSelector />
        </div>

        {user && (
          <div css={sidebarUser}>
            <div css={sidebarUserInfo}>
              <span css={sidebarUserName}>{user.username}</span>
              {user.email && <span css={sidebarUserEmail}>{user.email}</span>}
            </div>
            {onLogout && (
              <Button variant="outline" css={sidebarLogoutBtn} onClick={onLogout}>
                {t("layout.logout")}
              </Button>
            )}
          </div>
        )}
      </aside>

      {/* Main Content */}
      <main css={layoutMain}>
        <div css={layoutContent}>{children}</div>

        {/* Footer */}
        <footer css={layoutFooter}>
          <div css={footerContent}>
            <p>{t("layout.footer", { year: new Date().getFullYear() })}</p>
            <div css={footerLinks}>
              <span css={footerSeparator}>•</span>
              <a href="/about">{t("layout.about")}</a>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}
